import { Component, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { NzAffixComponent } from 'ng-zorro-antd/affix';
import { NzButtonComponent } from 'ng-zorro-antd/button';
import { NzFormControlComponent, NzFormDirective, NzFormItemComponent, NzFormLabelComponent } from 'ng-zorro-antd/form';
import { NzColDirective, NzRowDirective } from 'ng-zorro-antd/grid';
import { NzIconDirective } from 'ng-zorro-antd/icon';
import { NzInputDirective } from 'ng-zorro-antd/input';
import { NzNotificationService } from 'ng-zorro-antd/notification';
import { NzPageHeaderComponent, NzPageHeaderTitleDirective } from 'ng-zorro-antd/page-header';
import { NzPopconfirmDirective } from 'ng-zorro-antd/popconfirm';
import { NzOptionComponent, NzSelectComponent } from 'ng-zorro-antd/select';
import { NzSwitchComponent } from 'ng-zorro-antd/switch';
import { NzTableCellDirective, NzTableComponent, NzTbodyComponent, NzTheadComponent, NzThMeasureDirective, NzTrDirective } from 'ng-zorro-antd/table';
import { NzTagComponent } from 'ng-zorro-antd/tag';
import { NzUploadChangeParam, NzUploadComponent } from 'ng-zorro-antd/upload';
import { AppVersionService, MajorVersionModel } from '@adm-web-app/services/app-version.service';

/** Apps that ship OTA bundles; the name keys the descriptor stored on the backend. */
const APP_NAMES = ['HappyCakeShop', 'HappyCakeConnect'];

@Component({
  selector: 'hc-app-version-list',
  templateUrl: './list.component.html',
  imports: [
    FormsModule, NzAffixComponent, NzPageHeaderComponent, NzPageHeaderTitleDirective,
    NzRowDirective, NzColDirective, NzFormDirective, NzFormItemComponent, NzFormLabelComponent,
    NzFormControlComponent, NzSelectComponent, NzOptionComponent, NzInputDirective,
    NzButtonComponent, NzIconDirective, NzSwitchComponent, NzUploadComponent,
    NzTableComponent, NzTheadComponent, NzTbodyComponent, NzTrDirective,
    NzTableCellDirective, NzThMeasureDirective, NzTagComponent, NzPopconfirmDirective,
  ],
})
export class AppVersionListComponent implements OnInit {
  protected readonly appNames = APP_NAMES;
  protected appName = APP_NAMES[0];
  protected versions: MajorVersionModel[] = [];
  protected loading = false;

  /** Draft of the version being published; hash is filled by the upload. */
  protected form: { major?: number; version?: string; hash?: string; updateRequired: boolean; ru?: string; kk?: string } = {
    updateRequired: false,
  };

  private readonly service = inject(AppVersionService);
  private readonly notification = inject(NzNotificationService);

  get uploadUrl(): string {
    return this.service.uploadUrl;
  }

  ngOnInit(): void {
    void this.load();
  }

  async load() {
    this.loading = true;
    try {
      this.versions = await this.service.getVersions(this.appName);
    } catch (err) {
      this.notification.error('Ошибка', 'Не удалось загрузить список версий');
      console.error(err);
    } finally {
      this.loading = false;
    }
  }

  /** The endpoint answers with the archive's SHA-256 — that value identifies the bundle. */
  onUploadChange(event: NzUploadChangeParam) {
    if (event.type === 'success') {
      this.form.hash = event.file.response?.hash;
      this.notification.success('Готово', 'Бандл загружен');
    } else if (event.type === 'error') {
      this.notification.error('Ошибка', 'Не удалось загрузить бандл');
    }
  }

  async save() {
    if (!this.form.major || !this.form.version || !this.form.hash) {
      this.notification.warning('Проверьте поля', 'Нужны мажорная версия, версия бандла и загруженный архив');
      return;
    }
    try {
      await this.service.saveVersion({
        appName: this.appName,
        major: this.form.major,
        version: this.form.version,
        hash: this.form.hash,
        updateRequired: this.form.updateRequired,
        data: { ru: this.form.ru ?? '', kk: this.form.kk ?? '' },
      });
      this.notification.success('Готово', `Версия ${this.form.version} опубликована`);
      this.form = { updateRequired: false };
      await this.load();
    } catch (err) {
      this.notification.error('Ошибка', 'Не удалось опубликовать версию');
      console.error(err);
    }
  }

  /** Pre-fills the form from a row so an existing major can be re-published. */
  edit(version: MajorVersionModel) {
    this.form = {
      major: version.major,
      version: version.version,
      hash: version.hash,
      updateRequired: !!version.updateRequired,
      ru: version.data?.['ru'],
      kk: version.data?.['kk'],
    };
  }

  async remove(major: number) {
    try {
      await this.service.removeVersion(this.appName, major);
      await this.load();
    } catch (err) {
      this.notification.error('Ошибка', 'Не удалось удалить версию');
      console.error(err);
    }
  }
}
